import * as Locales from "date-fns/locale";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Col, Container, Form, Modal, ModalBody, Row } from "react-bootstrap";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { ButtonStyled, ModalClose, ModalMainHeader } from "shared/DesignSpecs/components/FormComponents";
import FormWrapper from "shared/components/FormWrapper";
import InputCalendarTS from "shared/components/InputCalendarTS";
import styled from 'styled-components';
import { useAppState } from "shared/AppState/app-state";
var ETAReason;
(function (ETAReason) {
    ETAReason["ADDITIONAL_REPAIRS_NEEDED"] = "ADDITIONAL_REPAIRS_NEEDED";
    ETAReason["BAY_TECHNICIAN_AVAILABILITY"] = "BAY_TECHNICIAN_AVAILABILITY";
    ETAReason["PARTS_ON_BACK_ORDER"] = "PARTS_ON_BACK_ORDER";
    ETAReason["VEHICLE_NOT_DROPPED_OFF_AS_SCHEDULED"] = "VEHICLE_NOT_DROPPED_OFF_AS_SCHEDULED";
    ETAReason["WAITING_FOR_HOLMAN_APPROVAL"] = "WAITING_FOR_HOLMAN_APPROVAL";
    ETAReason["WORK_COMPLETED"] = "WORK_COMPLETED";
    ETAReason["OTHER"] = "OTHER";
})(ETAReason || (ETAReason = {}));
//#endregion
// #region Styled Components
const ModalStyled = styled(Modal).attrs({
    dialogClassName: "childStyle"
}) `
.childStyle {
  width: 900px;
  max-width: 900px;
  min-height: 600px;
}
.childStyle > div > div {
  min-height: 10em;
}`;
const EtaContainer = styled(Container) `
  padding: 1rem 3rem 3rem 3rem;
  min-height: 10em;
`;
const NoteInput = styled.textarea `
  width:90%;
  height:10em;
  font-Weight:500;
  background-color: ${props => props.value === "" || props.value.length < 3 || props.value.length > 900 ? "#f8b6c7" : "none"}
`;
const StyledRadio = styled(Form.Check.Input) `
  border: none;
  width: 3rem;
  height: 3rem;
`;
const StyledReasonCol = styled(Col) `
  text-align: center;
  border-style: solid;
  border-color: #5587b7c2;
  padding: .25rem 1.5rem;
`;
const StyledReasonRow = styled(Row) `
  padding-bottom: 1rem;
  cursor: pointer;
`;
const StyledBodyRow = styled(Row) `
  padding: 0 4rem 0 3rem;
`;
const StyledHeaderCol = styled(Col) `
    font-size: 15px;
    font-family: "Gotham SSm A", "Gotham SSm B";
    font-style: normal;
    font-weight: 700;
`;
//#endregion
//#region Component
const ActionRequiredETAUpdate = ({ poNumber, showModal, toggleShowModal, dispatch, etaDate, setEtaDate, etaNote, setEtaNote, selectedReason, setSelectedReason, etaLineReviewed, setEtaLineReviewed }) => {
    // #region State
    const [errorMessages, setErrorMessages] = useState([]);
    const [updatedETADate, setUpdatedETADate] = useState(etaDate);
    const [updatedReason, setUpdatedReason] = useState(selectedReason);
    const [updatedNote, setUpdatedNote] = useState(etaNote);
    // #endregion
    // #region Ref
    // #endregion
    // #region Hooks
    const intl = useIntl();
    const [appState] = useAppState();
    // #endregion
    // #region Effects
    useEffect(() => {
        setUpdatedETADate(etaDate);
        setUpdatedReason(selectedReason);
        setUpdatedNote(etaNote);
    }, [showModal, etaDate, selectedReason, etaNote]);
    // #endregion
    // #region Event handlers
    const handleRadioChange = (reason) => {
        setUpdatedReason(reason);
        if (reason !== ETAReason.OTHER) {
            setUpdatedNote("");
        }
    };
    const handleEtaSubmitClicked = () => {
        let isValid = true;
        setErrorMessages(errorMessages => []);
        //Change the date to the end of the day
        let formattedMoment = updatedETADate ? moment(updatedETADate).endOf('day') : undefined;
        if (updatedETADate === undefined || moment(formattedMoment).isBefore(moment().startOf('day'))) {
            setErrorMessages(errorMessages => errorMessages.concat({ message: intl.formatMessage(messages.action_required_eta_date_error) }));
            isValid = false;
        }
        if (updatedReason === undefined) {
            setErrorMessages(errorMessages => errorMessages.concat({ message: intl.formatMessage(messages.action_required_eta_reason_error) }));
            isValid = false;
        }
        else if (updatedReason === ETAReason.OTHER) {
            if (updatedNote === "") {
                setErrorMessages(errorMessages => errorMessages.concat({ message: intl.formatMessage(messages.action_required_eta_note_error) }));
                isValid = false;
            }
            else if (updatedNote.length < 3 || updatedNote.length > 900) {
                setErrorMessages(errorMessages => errorMessages.concat({ message: intl.formatMessage(messages.action_required_eta_note_length_error) }));
                isValid = false;
            }
        }
        if (isValid) {
            setEtaLineReviewed(true);
            setEtaDate(formattedMoment === null || formattedMoment === void 0 ? void 0 : formattedMoment.toDate());
            setSelectedReason(updatedReason);
            setEtaNote(updatedNote);
            onCloseModal();
        }
    };
    const onCloseModal = () => {
        setErrorMessages([]);
        toggleShowModal();
    };
    // #endregion
    // #region International messages
    const messages = defineMessages({
        action_required_eta_title: {
            id: "action_required_eta_title",
            description: "Update repair ETA",
            defaultMessage: "Update repair ETA"
        },
        cancel: {
            id: "cancel",
            description: "Cancel",
            defaultMessage: "Cancel"
        },
        action_required_eta_body_1: {
            id: "action_required_eta_body_1",
            description: "Enter the estimated completion date for all repairs",
            defaultMessage: "Enter the estimated completion date for all repairs"
        },
        action_required_eta_body_2: {
            id: "action_required_eta_body_2",
            description: "Please select a reason for changing the estimated completion date",
            defaultMessage: "Please select a reason for changing the estimated completion date"
        },
        continue_link: {
            id: "continue_link",
            description: "Continue",
            defaultMessage: "Continue"
        },
        action_required_eta_date_error: {
            id: "action_required_eta_date_error",
            description: "Please enter a valid estimated completion date",
            defaultMessage: "Please enter a valid estimated completion date"
        },
        action_required_eta_reason_error: {
            id: "action_required_eta_reason_error",
            description: "Please select a reason for the change in repair ETA",
            defaultMessage: "Please select a reason for the change in repair ETA"
        },
        action_required_eta_note_error: {
            id: "action_required_eta_note_error",
            description: "Please enter a reason for the change in repair ETA",
            defaultMessage: "Please enter a reason for the change in repair ETA"
        },
        action_required_eta_note_length_error: {
            id: "action_required_eta_note_length_error",
            description: "Please enter a reason between 3-900 characters",
            defaultMessage: "Please enter a reason between 3-900 characters"
        },
        action_required_locked_po_2_message: {
            id: "action_required_locked_po_message",
            description: "PO is currently being reviewed by another Holman Technician.",
            defaultMessage: "PO is currently being reviewed by another Holman Technician."
        },
        action_required_locked_po_message: {
            id: "action_required_locked_po_message",
            description: "PO is currently being reviewed by a Holman Technician.",
            defaultMessage: "PO is currently being reviewed by a Holman Technician."
        },
        action_required_eta_reason_1: {
            id: "action_required_eta_reason_1",
            description: "Vehicle not dropped off as scheduled",
            defaultMessage: "Vehicle not dropped off as scheduled"
        },
        action_required_eta_reason_2: {
            id: "action_required_eta_reason_2",
            description: "Waiting for Holman approval",
            defaultMessage: "Waiting for Holman approval"
        },
        action_required_eta_reason_3: {
            id: "action_required_eta_reason_3",
            description: "Bay/Technician availablity",
            defaultMessage: "Bay/Technician availablity"
        },
        action_required_eta_reason_4: {
            id: "action_required_eta_reason_4",
            description: "Parts on back order",
            defaultMessage: "Parts on back order"
        },
        action_required_eta_reason_5: {
            id: "action_required_eta_reason_5",
            description: "Additional repairs found/needed",
            defaultMessage: "Additional repairs found/needed"
        },
        action_required_eta_reason_6: {
            id: "action_required_eta_reason_6",
            description: "Work completed",
            defaultMessage: "Work completed"
        },
        action_required_eta_reason_other: {
            id: "action_required_eta_reason_other",
            description: "Other",
            defaultMessage: "Other (please specify)"
        },
    });
    // #endregion
    // #region Render helpers
    const renderCloseButton = () => {
        return (React.createElement(ModalClose, { role: 'button', id: "close_btn", onClick: () => onCloseModal(), style: { width: "fit-content", marginBottom: "0.5rem" } },
            React.createElement("img", { src: "/assets/VMS_33button_kill_blue.png" }),
            "  ",
            " ",
            React.createElement(FormattedMessage, Object.assign({}, messages.cancel))));
    };
    const getLocalization = (userLocale) => {
        var _a;
        return (_a = Locales[userLocale.substring(0, 2)]) !== null && _a !== void 0 ? _a : Locales.enUS;
    };
    const renderReasonText = (reason) => {
        switch (reason) {
            case ETAReason.ADDITIONAL_REPAIRS_NEEDED:
                return React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_reason_5));
            case ETAReason.BAY_TECHNICIAN_AVAILABILITY:
                return React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_reason_3));
            case ETAReason.PARTS_ON_BACK_ORDER:
                return React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_reason_4));
            case ETAReason.VEHICLE_NOT_DROPPED_OFF_AS_SCHEDULED:
                return React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_reason_1));
            case ETAReason.WAITING_FOR_HOLMAN_APPROVAL:
                return React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_reason_2));
            case ETAReason.WORK_COMPLETED:
                return React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_reason_6));
            case ETAReason.OTHER:
                return React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_reason_other));
            default:
                return React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_reason_other));
        }
    };
    // #endregion  
    // #region Render
    const modifiers = {
        disabled: {
            before: new Date(new Date().toDateString())
        }
    };
    let calendarInputProps = { style: updatedETADate && !moment(updatedETADate).isBefore(moment().startOf('day')) ? { width: "100%" } : { backgroundColor: "#f8b6c7", width: "100%" } };
    return (React.createElement(ModalStyled, { show: showModal, backdrop: "static", keyboard: false, style: { overflow: "auto" } },
        React.createElement(ModalBody, null,
            renderCloseButton(),
            React.createElement(FormWrapper, { id: "etaSubmissionErrorMessages", key: "etaSubmissionErrorMessages", formErrors: errorMessages }),
            React.createElement(EtaContainer, null,
                React.createElement(ModalMainHeader, null,
                    React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_title))),
                React.createElement(StyledBodyRow, { className: "justify-content-center", style: { padding: "5rem 4rem 2rem 3rem" } },
                    React.createElement(StyledHeaderCol, { lg: 9 },
                        React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_body_1))),
                    React.createElement(Col, { lg: 3 },
                        React.createElement(InputCalendarTS, { modifiers: modifiers, initialValue: etaDate ? new Date(etaDate.toString()) : undefined, onDateChange: (day) => setUpdatedETADate(day), locale: intl.locale, id: "eta-input", inputProps: calendarInputProps }))),
                React.createElement(StyledBodyRow, null,
                    React.createElement("hr", { style: { borderTop: "1px solid #5587b7c2" } }),
                    React.createElement(StyledHeaderCol, { lg: 12, style: { paddingBottom: "3rem" } },
                        React.createElement(FormattedMessage, Object.assign({}, messages.action_required_eta_body_2)))),
                React.createElement(StyledBodyRow, { className: "justify-content-center" },
                    React.createElement(Form, null, Object.values(ETAReason).map((reason, index) => (React.createElement(StyledReasonRow, { key: reason, onClick: () => handleRadioChange(reason) },
                        React.createElement(Col, { lg: 1, md: 1, sm: 1 },
                            React.createElement(Form.Check, { type: 'radio', id: `radio${index + 1}`, name: `radio${index + 1}` },
                                React.createElement(StyledRadio, { isValid: true, value: reason, checked: updatedReason === reason, as: 'img', src: updatedReason === reason ? "/assets/VMS_checked.png" : "/assets/VMS_unchecked.png", tabIndex: 0, "aria-label": `Reason ${reason.replace(/_+/g, ' ')}` }))),
                        React.createElement(StyledReasonCol, { lg: 11, md: 8, sm: 6, style: { textAlign: "left" } }, renderReasonText(reason))))))),
                updatedReason === ETAReason.OTHER && (React.createElement(StyledBodyRow, { className: "justify-content-center" },
                    React.createElement(Col, { lg: { span: 11, offset: 1 }, style: { paddingLeft: "0" } },
                        React.createElement(NoteInput, { style: { height: "10rem", width: "100%" }, id: "note-input", value: updatedNote, onChange: (e) => setUpdatedNote(e.target.value), placeholder: null, "aria-label": "Additional notes input" })))),
                React.createElement(Row, { className: "justify-content-end" },
                    React.createElement(ButtonStyled, { role: "button", onClick: handleEtaSubmitClicked, style: { width: "fit-content", paddingRight: "0", paddingTop: "3.5rem" }, id: "submit_eta_btn" },
                        React.createElement(FormattedMessage, Object.assign({}, messages.continue_link)),
                        " ",
                        React.createElement("img", { src: "/assets/VMS_33button_go_blue.png" })))))));
    // #endregion
};
// #endregion
export default ActionRequiredETAUpdate;
